export type HudDropLane = 'left' | 'center' | 'right';
export type HudCameraPreset = 'front' | 'playfield' | 'tray' | 'home';
export type HudStatusTone = 'neutral' | 'win' | 'loss' | 'warning' | 'jackpot';
export type HudOverlay = 'none' | 'game-over' | 'jackpot';

export interface CoinPusherHudSnapshot {
  readonly balance: number;
  readonly cards: {
    readonly collected: number;
    readonly setSize: number;
    readonly completedSets: number;
  };
  readonly jackpot: {
    readonly value: number;
    /** Normalized progress from 0 to 1. */
    readonly progress: number;
  };
  readonly sessionWins: number;
  readonly tray: {
    readonly totalItems: number;
    readonly coins: number;
    readonly cards: number;
    readonly collectableItems: number;
    readonly lastCollectedCoins: number;
    readonly lastCollectedCards: number;
    readonly lastBalanceGain: number;
    readonly collectionSequence: number;
  };
  readonly status: {
    readonly kicker: string;
    readonly message: string;
    readonly tone: HudStatusTone;
    /** Change this value to replay the compact event-banner animation. */
    readonly eventId?: string;
  };
  readonly selectedLane: HudDropLane;
  readonly cameraPreset: HudCameraPreset;
  readonly paused: boolean;
  readonly muted: boolean;
  readonly canInsertCoin: boolean;
  readonly canRestart: boolean;
  readonly loading: boolean;
  readonly loadingMessage?: string;
  readonly overlay: HudOverlay;
  readonly gameOver?: {
    readonly title: string;
    readonly summary: string;
  };
  readonly jackpotCelebration?: {
    readonly value: number;
    readonly message: string;
  };
}

export interface CoinPusherHudActions {
  readonly onInsertCoin?: () => void;
  readonly onLaneChange?: (lane: HudDropLane) => void;
  readonly onCameraChange?: (preset: HudCameraPreset) => void;
  readonly onPauseToggle?: () => void;
  readonly onResume?: () => void;
  readonly onMuteToggle?: () => void;
  readonly onRestart?: () => void;
  readonly onJackpotContinue?: () => void;
  readonly onCollectTray?: () => void;
}

export interface CoinPusherHudButtons {
  readonly insertCoin: HTMLButtonElement;
  readonly lanes: Readonly<Record<HudDropLane, HTMLButtonElement>>;
  readonly cameras: Readonly<Record<HudCameraPreset, HTMLButtonElement>>;
  readonly pause: HTMLButtonElement;
  readonly sound: HTMLButtonElement;
  readonly restart: HTMLButtonElement;
  readonly resume: HTMLButtonElement;
  readonly pauseRestart: HTMLButtonElement;
  readonly gameOverRestart: HTMLButtonElement;
  readonly jackpotContinue: HTMLButtonElement;
  readonly collectTray: HTMLButtonElement;
}

export const DEFAULT_COIN_PUSHER_HUD_SNAPSHOT: CoinPusherHudSnapshot = Object.freeze({
  balance: 25,
  cards: Object.freeze({ collected: 0, setSize: 5, completedSets: 0 }),
  jackpot: Object.freeze({ value: 2500, progress: 0 }),
  sessionWins: 0,
  tray: Object.freeze({
    totalItems: 0,
    coins: 0,
    cards: 0,
    collectableItems: 0,
    lastCollectedCoins: 0,
    lastCollectedCards: 0,
    lastBalanceGain: 0,
    collectionSequence: 0,
  }),
  status: Object.freeze({
    kicker: 'Machine',
    message: 'Ready for play',
    tone: 'neutral' as const,
    eventId: 'initial',
  }),
  selectedLane: 'center',
  cameraPreset: 'front',
  paused: false,
  muted: false,
  canInsertCoin: true,
  canRestart: true,
  loading: true,
  loadingMessage: 'Polishing chrome and stacking medals…',
  overlay: 'none',
});

type ActiveModal = HudOverlay | 'pause';

/**
 * DOM-only HUD bridge. It renders a typed game snapshot and dispatches button
 * intents; scoring, progression, camera, audio, and pause rules remain owned by
 * their gameplay systems.
 */
export class Hud {
  readonly buttons: CoinPusherHudButtons;

  private readonly hudRoot: HTMLElement;
  private readonly balanceValue: HTMLElement;
  private readonly cardCount: HTMLElement;
  private readonly cardSetSize: HTMLElement;
  private readonly completedSets: HTMLElement;
  private readonly jackpotValue: HTMLElement;
  private readonly jackpotMeter: HTMLElement;
  private readonly jackpotFill: HTMLElement;
  private readonly trayLabel: HTMLElement;
  private readonly trayCount: HTMLElement;
  private readonly trayAction: HTMLElement;
  private readonly trayLastCollection: HTMLElement;
  private readonly statusBanner: HTMLElement;
  private readonly statusKicker: HTMLElement;
  private readonly statusMessage: HTMLElement;
  private readonly loadingOverlay: HTMLElement;
  private readonly loadingMessage: HTMLElement;
  private readonly pauseOverlay: HTMLElement;
  private readonly gameOverOverlay: HTMLElement;
  private readonly gameOverTitle: HTMLElement;
  private readonly gameOverSummary: HTMLElement;
  private readonly jackpotOverlay: HTMLElement;
  private readonly jackpotCelebrationValue: HTMLElement;
  private readonly jackpotCelebrationMessage: HTMLElement;

  private actionAbort?: AbortController;
  private lastStatusKey = '';
  private lastCollectionSequence = 0;
  private activeModal: ActiveModal = 'none';
  private legacyTarget = 5;

  constructor(private readonly root: ParentNode = document) {
    this.hudRoot = this.getElement('#hud');
    this.balanceValue = this.getElement('#hud-balance-value');
    this.cardCount = this.getElement('#hud-card-count');
    this.cardSetSize = this.getElement('#hud-card-set-size');
    this.completedSets = this.getElement('#hud-completed-sets');
    this.jackpotValue = this.getElement('#hud-jackpot-value');
    this.jackpotMeter = this.getElement('#hud-jackpot-meter');
    this.jackpotFill = this.getElement('#hud-jackpot-fill');
    this.trayLabel = this.getElement('#hud-tray-label');
    this.trayCount = this.getElement('#hud-tray-count');
    this.trayAction = this.getElement('#hud-tray-action');
    this.trayLastCollection = this.getElement('#hud-tray-last-collection');
    this.statusBanner = this.getElement('#hud-status-banner');
    this.statusKicker = this.getElement('#hud-status-kicker');
    this.statusMessage = this.getElement('#hud-status-message');
    this.loadingOverlay = this.getElement('#loading-overlay');
    this.loadingMessage = this.getElement('#loading-message');
    this.pauseOverlay = this.getElement('#pause-overlay');
    this.gameOverOverlay = this.getElement('#game-over-overlay');
    this.gameOverTitle = this.getElement('#game-over-title');
    this.gameOverSummary = this.getElement('#game-over-summary');
    this.jackpotOverlay = this.getElement('#jackpot-overlay');
    this.jackpotCelebrationValue = this.getElement('#jackpot-celebration-value');
    this.jackpotCelebrationMessage = this.getElement('#jackpot-celebration-message');

    this.buttons = Object.freeze({
      insertCoin: this.getButton('#insert-coin-button'),
      lanes: Object.freeze({
        left: this.getButton('#lane-left-button'),
        center: this.getButton('#lane-center-button'),
        right: this.getButton('#lane-right-button'),
      }),
      cameras: Object.freeze({
        front: this.getButton('#camera-front-button'),
        playfield: this.getButton('#camera-playfield-button'),
        tray: this.getButton('#camera-tray-button'),
        home: this.getButton('#camera-home-button'),
      }),
      pause: this.getButton('#pause-button'),
      sound: this.getButton('#sound-button'),
      restart: this.getButton('#restart-button'),
      resume: this.getButton('#resume-button'),
      pauseRestart: this.getButton('#pause-restart-button'),
      gameOverRestart: this.getButton('#game-over-restart-button'),
      jackpotContinue: this.getButton('#jackpot-continue-button'),
      collectTray: this.getButton('#collect-tray-button'),
    });

    this.render(DEFAULT_COIN_PUSHER_HUD_SNAPSHOT);
  }

  update(snapshot: CoinPusherHudSnapshot): void;
  /** Temporary compatibility signature for the outgoing scaffold Game. */
  update(score: number, target: number, elapsed: number, complete: boolean): void;
  update(
    snapshotOrScore: CoinPusherHudSnapshot | number,
    target = this.legacyTarget,
    _elapsed = 0,
    complete = false,
  ): void {
    if (typeof snapshotOrScore !== 'number') {
      this.render(snapshotOrScore);
      return;
    }

    this.legacyTarget = target;
    const score = Math.max(0, snapshotOrScore);
    const progress = target > 0 ? Math.min(1, score / target) : 0;
    this.render({
      ...DEFAULT_COIN_PUSHER_HUD_SNAPSHOT,
      balance: Math.max(0, target - score),
      cards: { collected: score, setSize: target, completedSets: complete ? 1 : 0 },
      jackpot: { value: 2500 + score * 25, progress },
      sessionWins: complete ? 1 : 0,
      status: {
        kicker: complete ? 'System' : 'Machine',
        message: complete ? 'Collection complete' : 'Ready for play',
        tone: complete ? 'win' : 'neutral',
        eventId: complete ? 'legacy-complete' : 'legacy-ready',
      },
      loading: false,
    });
  }

  /** Temporary compatibility method for the outgoing scaffold Game. */
  setTarget(target: number): void {
    this.legacyTarget = Math.max(0, target);
    this.cardSetSize.textContent = this.formatCounter(this.legacyTarget, 2);
  }

  /** Compact event feedback hook retained for the current integration point. */
  flashPickup(): void {
    this.renderStatus({
      kicker: 'Payout',
      message: 'Item collected',
      tone: 'win',
      eventId: `pickup-${performance.now()}`,
    });
  }

  bindActions(actions: CoinPusherHudActions): () => void {
    this.actionAbort?.abort();
    const controller = new AbortController();
    this.actionAbort = controller;
    const listen = (button: HTMLButtonElement, action: (() => void) | undefined): void => {
      if (action) button.addEventListener('click', action, { signal: controller.signal });
    };

    listen(this.buttons.insertCoin, actions.onInsertCoin);
    for (const lane of ['left', 'center', 'right'] as const) {
      listen(this.buttons.lanes[lane], actions.onLaneChange ? () => actions.onLaneChange?.(lane) : undefined);
    }
    for (const preset of ['front', 'playfield', 'tray', 'home'] as const) {
      listen(
        this.buttons.cameras[preset],
        actions.onCameraChange ? () => actions.onCameraChange?.(preset) : undefined,
      );
    }
    listen(this.buttons.pause, actions.onPauseToggle);
    listen(this.buttons.resume, actions.onResume ?? actions.onPauseToggle);
    listen(this.buttons.sound, actions.onMuteToggle);
    listen(this.buttons.restart, actions.onRestart);
    listen(this.buttons.pauseRestart, actions.onRestart);
    listen(this.buttons.gameOverRestart, actions.onRestart);
    listen(this.buttons.jackpotContinue, actions.onJackpotContinue);
    listen(this.buttons.collectTray, actions.onCollectTray);

    return () => {
      controller.abort();
      if (this.actionAbort === controller) this.actionAbort = undefined;
    };
  }

  dispose(): void {
    this.actionAbort?.abort();
    this.actionAbort = undefined;
  }

  private render(snapshot: CoinPusherHudSnapshot): void {
    const jackpotProgress = Math.min(1, Math.max(0, snapshot.jackpot.progress));
    this.balanceValue.textContent = this.formatCounter(snapshot.balance, 3);
    this.cardCount.textContent = this.formatCounter(snapshot.cards.collected, 2);
    this.cardSetSize.textContent = this.formatCounter(snapshot.cards.setSize, 2);
    this.completedSets.textContent = this.formatCounter(snapshot.cards.completedSets, 2);
    this.jackpotValue.textContent = this.formatCounter(snapshot.jackpot.value, 7);
    this.jackpotFill.style.setProperty('--meter-progress', `${(jackpotProgress * 100).toFixed(1)}%`);
    this.jackpotMeter.setAttribute('aria-valuenow', String(Math.round(jackpotProgress * 100)));
    const hasCollectionReceipt = snapshot.tray.collectionSequence > 0;
    const showCollectionReceipt = snapshot.tray.totalItems === 0 && hasCollectionReceipt;
    this.trayLabel.textContent = showCollectionReceipt ? 'Recovered' : 'Prize Tray';
    this.trayCount.textContent = showCollectionReceipt
      ? `+${this.formatCounter(snapshot.tray.lastBalanceGain, 3)}`
      : this.formatCounter(snapshot.tray.totalItems, 3);
    this.trayAction.textContent = showCollectionReceipt
      ? `${snapshot.tray.lastCollectedCoins} medals · ${snapshot.tray.lastCollectedCards} cards`
      : snapshot.tray.totalItems === 0
        ? 'Empty'
        : snapshot.tray.collectableItems > 0
          ? 'Collect'
          : 'Landing…';
    this.trayLastCollection.textContent = hasCollectionReceipt
      ? `Last +${this.formatCounter(snapshot.tray.lastBalanceGain, 3)}`
      : 'No collection yet';
    this.trayLastCollection.title = hasCollectionReceipt
      ? `Last collection added ${snapshot.tray.lastBalanceGain} coins to the balance.`
      : 'No winnings collected yet.';
    this.buttons.collectTray.dataset.hasReceipt = String(hasCollectionReceipt);
    this.buttons.collectTray.dataset.receipt = String(showCollectionReceipt);
    if (snapshot.tray.collectionSequence !== this.lastCollectionSequence) {
      this.lastCollectionSequence = snapshot.tray.collectionSequence;
      this.trayLastCollection.classList.remove('is-fresh');
      void this.trayLastCollection.offsetWidth;
      this.trayLastCollection.classList.add('is-fresh');
    }
    this.renderStatus(snapshot.status);

    for (const lane of ['left', 'center', 'right'] as const) {
      this.buttons.lanes[lane].setAttribute('aria-pressed', String(snapshot.selectedLane === lane));
    }
    for (const preset of ['front', 'playfield', 'tray', 'home'] as const) {
      this.buttons.cameras[preset].setAttribute('aria-pressed', String(snapshot.cameraPreset === preset));
    }

    this.buttons.pause.setAttribute('aria-pressed', String(snapshot.paused));
    this.buttons.pause.setAttribute('aria-label', snapshot.paused ? 'Resume game' : 'Pause game');
    this.buttons.sound.dataset.muted = String(snapshot.muted);
    this.buttons.sound.setAttribute('aria-pressed', String(snapshot.muted));
    this.buttons.sound.setAttribute('aria-label', snapshot.muted ? 'Unmute sound' : 'Mute sound');

    const modalOpen = snapshot.loading || snapshot.paused || snapshot.overlay !== 'none';
    for (const button of Object.values(this.buttons.lanes)) button.disabled = modalOpen;
    for (const button of Object.values(this.buttons.cameras)) button.disabled = modalOpen;
    this.buttons.insertCoin.disabled = modalOpen || !snapshot.canInsertCoin || snapshot.balance <= 0;
    this.buttons.collectTray.disabled = modalOpen || snapshot.tray.collectableItems <= 0;
    this.buttons.collectTray.dataset.ready = String(snapshot.tray.collectableItems > 0);
    this.buttons.collectTray.setAttribute(
      'aria-label',
      `${snapshot.tray.totalItems === 0
        ? 'Prize tray empty.'
        : `${snapshot.tray.coins} coins and ${snapshot.tray.cards} bonus cards in prize tray. ${snapshot.tray.collectableItems} ready to collect.`}${hasCollectionReceipt
        ? ` Last collection: ${snapshot.tray.lastCollectedCoins} coins and ${snapshot.tray.lastCollectedCards} bonus cards, adding ${snapshot.tray.lastBalanceGain} coins to the balance.`
        : ''}`,
    );
    this.buttons.pause.disabled = snapshot.loading || snapshot.overlay !== 'none';
    this.buttons.restart.disabled = snapshot.loading || !snapshot.canRestart;

    this.loadingMessage.textContent = snapshot.loadingMessage ?? 'Polishing chrome and stacking medals…';
    this.loadingOverlay.hidden = !snapshot.loading;

    const showPause = !snapshot.loading && snapshot.paused && snapshot.overlay === 'none';
    const showGameOver = !snapshot.loading && snapshot.overlay === 'game-over';
    const showJackpot = !snapshot.loading && snapshot.overlay === 'jackpot';
    this.pauseOverlay.hidden = !showPause;
    this.gameOverOverlay.hidden = !showGameOver;
    this.jackpotOverlay.hidden = !showJackpot;
    this.gameOverTitle.textContent = snapshot.gameOver?.title ?? 'Out of Coins';
    this.gameOverSummary.textContent = snapshot.gameOver?.summary
      ?? 'The tray is quiet. Reload your balance and push again.';
    this.jackpotCelebrationValue.textContent = this.formatCounter(
      snapshot.jackpotCelebration?.value ?? snapshot.jackpot.value,
      7,
    );
    this.jackpotCelebrationMessage.textContent = snapshot.jackpotCelebration?.message
      ?? 'A perfect push lights the whole arcade.';

    const nextModal: ActiveModal = showJackpot ? 'jackpot' : showGameOver ? 'game-over' : showPause ? 'pause' : 'none';
    this.hudRoot.setAttribute('aria-hidden', String(nextModal !== 'none' || snapshot.loading));
    if (nextModal !== this.activeModal) {
      this.activeModal = nextModal;
      const focusTarget = nextModal === 'jackpot'
        ? this.buttons.jackpotContinue
        : nextModal === 'game-over'
          ? this.buttons.gameOverRestart
          : nextModal === 'pause'
            ? this.buttons.resume
            : undefined;
      focusTarget?.focus({ preventScroll: true });
    }
  }

  private renderStatus(status: CoinPusherHudSnapshot['status']): void {
    this.statusKicker.textContent = status.kicker;
    this.statusMessage.textContent = status.message;
    this.statusBanner.dataset.tone = status.tone;
    const statusKey = status.eventId ?? `${status.tone}:${status.kicker}:${status.message}`;
    if (statusKey !== this.lastStatusKey) {
      this.lastStatusKey = statusKey;
      this.statusBanner.classList.remove('is-fresh');
      void this.statusBanner.offsetWidth;
      this.statusBanner.classList.add('is-fresh');
    }
  }

  private formatCounter(value: number, minimumDigits: number): string {
    const safeValue = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
    return safeValue.toLocaleString('en-US').padStart(minimumDigits, '0');
  }

  private getButton(selector: string): HTMLButtonElement {
    return this.getElement<HTMLButtonElement>(selector);
  }

  private getElement<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
